import React, { useMemo, useCallback, useState, useRef } from "react";
import { scaleTime, scaleLinear } from "@visx/scale";
import { Brush } from "@visx/brush";
import { PatternLines } from "@visx/pattern";
import { Group } from "@visx/group";
import { LinearGradient } from "@visx/gradient";
import AreaChart from "./JourneyAreaChart";
import { max, min, extent, bisector } from "d3-array";
import { Line, Bar } from "@visx/shape";
import { timeFormat } from "@visx/vendor/d3-time-format";
import { withTooltip, defaultStyles, TooltipWithBounds } from "@visx/tooltip";
import { GlyphTriangle } from "@visx/glyph";
import { localPoint } from "@visx/event";
import ImageOverlay from "../components/ImageOverlay";
import useUnits from "../hooks/useUnits";

const brushMargin = { top: 10, bottom: 0, left: 50, right: 20 };
const chartSeparation = 5;
const PATTERN_ID = "brush_pattern";
const GRADIENT_ID = "brush_gradient";
const background = "#0085cdd6";
const accentColor = "#0085cd";
const fromBackgroundGradient = "#fff";
const toBackgroundGradient = "#fff";
const strokeLineSelected = "red";

const selectedBrushStyle = {
  fill: `url(#${PATTERN_ID})`,
  stroke: "#868686",
};
const tooltipStyles = {
  ...defaultStyles,
  background,
  border: "1px solid white",
  color: "white",
};

const formatTimeFull = timeFormat("%H:%M");
const getDate = (d) => new Date(d);
const getDateDataValue = (d) => getDate(d.timestamp);
const getSpeedDataValue = (d) => d.speed;
const bisectDate = bisector((d) => getDateDataValue(d)).left;

const JourneyGraph = withTooltip(
  ({
    compact = false,
    width,
    height,
    margin = {
      top: 20,
      left: 25,
      bottom: 0,
      right: 0,
    },
    vehicleId,
    speedData,
    onTimeSelected,
    onSafetyEventSelected,
    timeSelected,
    safetyEventData,
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipTop = 0,
    tooltipLeft = 0,
    dateSelected,
  }) => {
    const brushRef = useRef(null);
    const [mousePosition, setMousePosition] = useState(null);
    const { formatSpeed } = useUnits();

    const speedDataCopy = [...speedData];

    const newSpeedData = speedDataCopy.sort(
      (a, b) => getDate(b.timestamp) - getDate(a.timestamp),
    );

    const enableBrush = newSpeedData.some((item) => item.speed > 0);

    const [filteredSpeedData, setFilteredSpeedData] = useState(newSpeedData);
    // Time range the top chart is zoomed to; null = full day.
    const [zoomDateDomain, setZoomDateDomain] = useState(null);

    const onBrushChange = (domain) => {
      if (!domain) return;
      const { x0, x1 } = domain;

      // Zoom by time only; keep zero-speed points so stops/ramps stay visible.
      const speedDataWithinBrushRange = newSpeedData.filter((s) => {
        const x = getDateDataValue(s).getTime();
        return x >= x0 && x <= x1;
      });

      if (!speedDataWithinBrushRange.length) return;

      setFilteredSpeedData(speedDataWithinBrushRange);
      setZoomDateDomain([new Date(x0), new Date(x1)]);
    };

    const innerHeight = height - margin.top - margin.bottom;
    const topChartBottomMargin = compact
      ? chartSeparation / 2
      : chartSeparation + 10;
    const topChartHeight = 0.8 * innerHeight - topChartBottomMargin;
    const bottomChartHeight = innerHeight - topChartHeight - chartSeparation;

    // bounds
    const xMax = Math.max(width - margin.left - margin.right, 0);
    const yMax = Math.max(enableBrush ? topChartHeight : innerHeight, 0);
    const xBrushMax = Math.max(width - brushMargin.left - brushMargin.right, 0);
    const yBrushMax = Math.max(
      bottomChartHeight - brushMargin.top - brushMargin.bottom,
      0,
    );

    // Fixed 00:00 - 23:59
    const fullDayDomain = useMemo(() => {
      const dayStart = new Date(dateSelected);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dateSelected);
      dayEnd.setHours(23, 59, 59, 999);
      return [dayStart, dayEnd];
    }, [dateSelected]);

    // scales
    const dateValueScale = useMemo(
      () =>
        scaleTime({
          range: [0, xMax],
          domain: zoomDateDomain || fullDayDomain,
        }),
      [xMax, fullDayDomain, zoomDateDomain],
    );

    const speedValueScale = useMemo(
      () =>
        scaleLinear({
          range: [yMax, 0],
          domain: [0, max(filteredSpeedData, getSpeedDataValue) || 0],
          nice: true,
        }),
      [yMax, filteredSpeedData],
    );

    const brushDateValueScale = useMemo(
      () =>
        scaleTime({
          range: [0, xBrushMax],
          domain: fullDayDomain,
        }),
      [xBrushMax, fullDayDomain],
    );

    const brushSpeedValueScale = useMemo(
      () =>
        scaleLinear({
          range: [yBrushMax, 0],
          domain: [0, max(newSpeedData, getSpeedDataValue) || 0],
          nice: true,
        }),
      [yBrushMax],
    );

    // positions
    const getX = (d) => dateValueScale(new Date(d)) ?? 0;

    // tooltip handler
    const handleSelect = useCallback(
      (event) => {
        const { x } = localPoint(event) || { x: 0 };
        var xSubtractMargin = x - margin.left;
        const x0 = dateValueScale.invert(xSubtractMargin);
        var index = bisectDate(speedData, x0, 1);
        const d0 = speedData[index - 1];
        const d1 = speedData[index];
        let d = d0;
        if (d1 && getDateDataValue(d1)) {
          index =
            x0.valueOf() - getDateDataValue(d0).valueOf() >
            getDateDataValue(d1).valueOf() - x0.valueOf()
              ? index
              : index - 1;

          d = speedData[index];
        }

        onTimeSelected(x0);
      },
      [speedValueScale, dateValueScale],
    );

    const handleTooltip = useCallback(
      (event) => {
        const point = localPoint(event);
        const { x, y } = point || { x: 0, y: 0 };
        var xSubtractMargin = x - margin.left;
        const x0 = dateValueScale.invert(xSubtractMargin);
        var index = bisectDate(speedData, x0, 1);
        const d0 = speedData[index - 1];
        const d1 = speedData[index];

        const d =
          d1 && getDateDataValue(d1)
            ? x0 - getDateDataValue(d0).valueOf() >
              getDateDataValue(d1).valueOf() - x0
              ? d1
              : d0
            : { ...d0, speed: 0 };

        const clientX = event.clientX || event.touches?.[0]?.clientX || 0;
        const clientY = event.clientY || event.touches?.[0]?.clientY || 0;

        const imageWidth = 360;
        const imageHeight = 203;
        const padding = 10;

        let posX = clientX - imageWidth / 2 - 200;
        if (posX < padding) {
          posX = padding;
        } else if (posX + imageWidth > window.innerWidth - padding) {
          posX = window.innerWidth - imageWidth - padding;
        }

        let posY = posY < padding ? padding : clientY - imageHeight - 80;
        setMousePosition({
          x: posX,
          y: posY,
        });

        showTooltip({
          tooltipData: { ...d, timestamp: x0 },
          tooltipLeft: xSubtractMargin,
          tooltipTop: speedValueScale(getSpeedDataValue(d)),
        });
      },

      [showTooltip, speedValueScale, dateValueScale],
    );

    return (
      <div>
        <svg width={width} height={height}>
          <LinearGradient
            id={GRADIENT_ID}
            from={fromBackgroundGradient}
            to={toBackgroundGradient}
            rotate={45}
          />
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill={`url(#${GRADIENT_ID})`}
            rx={14}
          />
          <AreaChart
            hideBottomAxis={compact}
            data={filteredSpeedData}
            width={width}
            margin={{ ...margin, bottom: topChartBottomMargin }}
            yMax={yMax}
            xMax={xMax}
            xScale={dateValueScale}
            yScale={speedValueScale}
            gradientColor={background}
          >
            <Bar
              x={0}
              y={0}
              width={width}
              height={height}
              fill="transparent"
              rx={14}
              onClick={handleSelect}
              onTouchStart={handleTooltip}
              onTouchMove={handleTooltip}
              onMouseMove={handleTooltip}
              onMouseLeave={() => {
                hideTooltip();
                setMousePosition(null);
              }}
            />
            {timeSelected && (
              <Line
                from={{ x: getX(timeSelected), y: -margin.top }}
                to={{ x: getX(timeSelected), y: innerHeight - brushMargin.top }}
                stroke={strokeLineSelected}
                strokeWidth={2}
                pointerEvents="none"
                strokeDasharray="0"
              />
            )}
            {safetyEventData?.totalCount > 0 &&
              safetyEventData.edges.map(({ cursor, node }) => (
                <GlyphTriangle
                  key={cursor}
                  left={getX(node.occurredAt)}
                  top={margin.top + 32}
                  fill="#f90"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    onSafetyEventSelected(node);
                  }}
                />
              ))}
            {tooltipData && (
              <g>
                <Line
                  from={{ x: tooltipLeft, y: 0 }}
                  to={{ x: tooltipLeft, y: innerHeight }}
                  stroke={accentColor}
                  strokeWidth={1}
                  pointerEvents="none"
                  strokeDasharray="0"
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop + 1}
                  r={4}
                  fill="black"
                  fillOpacity={0.1}
                  stroke="black"
                  strokeOpacity={0.1}
                  strokeWidth={2}
                  pointerEvents="none"
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop}
                  r={4}
                  fill={accentColor}
                  stroke="white"
                  strokeWidth={2}
                  pointerEvents="none"
                />
              </g>
            )}
          </AreaChart>
          {enableBrush && (
            <AreaChart
              hideTopAxis
              hideGridRowAxis
              hideLeftAxis
              data={newSpeedData}
              width={width}
              yMax={yBrushMax}
              xMax={xBrushMax}
              xScale={brushDateValueScale}
              yScale={brushSpeedValueScale}
              margin={brushMargin}
              top={topChartHeight + topChartBottomMargin + margin.top}
              gradientColor={background}
            >
              <PatternLines
                id={PATTERN_ID}
                height={6}
                width={6}
                stroke={accentColor}
                strokeWidth={1}
                orientation={["diagonal"]}
              />
              <Brush
                xScale={brushDateValueScale}
                yScale={brushSpeedValueScale}
                width={xBrushMax}
                height={yBrushMax}
                margin={brushMargin}
                innerRef={brushRef}
                handleSize={8}
                resizeTriggerAreas={["left", "right"]}
                brushDirection="horizontal"
                onChange={onBrushChange}
                onClick={() => {
                  setFilteredSpeedData(newSpeedData);
                  setZoomDateDomain(null);
                }}
                selectedBoxStyle={selectedBrushStyle}
                useWindowMoveEvents
                renderBrushHandle={(props) => <BrushHandle {...props} />}
              />
            </AreaChart>
          )}
        </svg>

        {tooltipData && (
          <TooltipWithBounds
            top={height / 5}
            left={tooltipLeft + margin.left}
            style={tooltipStyles}
          >
            <div style={{ marginBottom: "5px" }}>
              {formatTimeFull(getDateDataValue(tooltipData))}
            </div>
            <div>Speed: {formatSpeed(getSpeedDataValue(tooltipData))}</div>
          </TooltipWithBounds>
        )}

        <ImageOverlay
          vehicleId={vehicleId}
          timestamp={tooltipData?.timestamp}
          show={!!tooltipData}
          mousePosition={mousePosition}
        />
      </div>
    );
  },
);

function BrushHandle({ x, height, isBrushActive }) {
  const pathWidth = 8;
  const pathHeight = 15;
  if (!isBrushActive) {
    return null;
  }
  return (
    <Group left={x + pathWidth / 2} top={(height - pathHeight) / 2}>
      <path
        fill="#f2f2f2"
        d="M -4.5 0.5 L 3.5 0.5 L 3.5 15.5 L -4.5 15.5 L -4.5 0.5 M -1.5 4 L -1.5 12 M 0.5 4 L 0.5 12"
        stroke="#999999"
        strokeWidth="1"
        style={{ cursor: "ew-resize" }}
      />
    </Group>
  );
}

export default JourneyGraph;
